import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import type {
  CreateStorageLocationPayload,
  StorageLocationNode
} from '../Types';
import { cn } from '@/shared/utils/helpers';
import {
  getCreatableChildLocationType,
  getLocationTypeLabel,
  isSystemDefaultStorageLocation
} from '../Utils/StorageLocation.utils';
import { AddStorageLocationForm } from './AddStorageLocationForm';

const locationCheckboxClass =
  'h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-2 border-[#b9bfc7] bg-white transition hover:border-[#087f8c] checked:border-[#087f8c] checked:bg-[#087f8c] disabled:cursor-not-allowed disabled:opacity-40';

export interface StorageTreeProps {
  nodes: StorageLocationNode[];
  depth?: number;
  collapsedIds: Set<string>;
  addParentId: string | null;
  creatingLocation?: boolean;
  /** When set, rows show a hover/selected checkbox for assignment. */
  selectedLocationId?: string | null;
  selectionEnabled?: boolean;
  onSelectLocation?: (node: StorageLocationNode) => void;
  onToggleCollapse: (id: string) => void;
  onRequestAdd: (node: StorageLocationNode) => void;
  onCancelAdd: () => void;
  onCreated: (parentId: string) => void;
  onCreateLocation?: (
    payload: CreateStorageLocationPayload
  ) => Promise<StorageLocationNode>;
}

export const StorageTree = ({
  nodes,
  depth = 0,
  collapsedIds,
  addParentId,
  creatingLocation,
  selectedLocationId = null,
  selectionEnabled = false,
  onSelectLocation,
  onToggleCollapse,
  onRequestAdd,
  onCancelAdd,
  onCreated,
  onCreateLocation
}: StorageTreeProps) => (
  <div>
    {nodes
      .filter(node => node.active && !isSystemDefaultStorageLocation(node))
      .map(node => {
        const children = (node.children ?? []).filter(
          child => child.active && !isSystemDefaultStorageLocation(child)
        );
        const hasChildren = children.length > 0;
        const isCollapsed = collapsedIds.has(node.id);
        const childType = getCreatableChildLocationType(node);
        const indent = depth * 18;
        const isSelected = selectedLocationId === node.id;

        return (
          <div key={node.id}>
            <div
              className={cn(
                'group flex items-center gap-1.5 rounded-md py-1.5 pr-1.5 transition',
                isSelected
                  ? 'bg-[#087f8c]/[0.08] ring-1 ring-[#087f8c]/25'
                  : 'hover:bg-[#087f8c]/[0.05]'
              )}
              style={{ paddingLeft: 6 + indent }}
            >
              {selectionEnabled && onSelectLocation ? (
                <label
                  title={`Assign to ${node.name}`}
                  className={cn(
                    'grid h-6 w-6 shrink-0 place-items-center transition',
                    isSelected
                      ? 'opacity-100'
                      : 'opacity-40 group-hover:opacity-100 focus-within:opacity-100'
                  )}
                  onClick={event => event.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    className={locationCheckboxClass}
                    checked={isSelected}
                    disabled={creatingLocation}
                    onChange={() => onSelectLocation(node)}
                    aria-label={`Select ${node.name}`}
                  />
                </label>
              ) : null}

              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => onToggleCollapse(node.id)}
                  aria-label={
                    isCollapsed
                      ? `Expand ${node.name}`
                      : `Collapse ${node.name}`
                  }
                  className="grid h-5 w-5 shrink-0 place-items-center rounded text-[#8a8f96] transition hover:bg-[#087f8c]/10 hover:text-[#087f8c]"
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
              ) : (
                <span className="grid h-5 w-5 shrink-0 place-items-center">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#d3d7dd]" />
                </span>
              )}

              <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#171717]">
                {node.name}
                {node.code ? (
                  <span className="ml-1.5 text-[11px] font-normal text-[#a0a5ac]">
                    {node.code}
                  </span>
                ) : null}
              </span>

              <span className="shrink-0 rounded-full bg-[#f0f2f5] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#6f747c]">
                {node.location_type}
              </span>
              <span className="w-14 shrink-0 text-right text-xs tabular-nums text-[#8a8f96]">
                {node.item_count ?? 0} items
              </span>

              {childType && onCreateLocation ? (
                <button
                  type="button"
                  onClick={() => onRequestAdd(node)}
                  title={`Add ${getLocationTypeLabel(childType).toLowerCase()} in ${node.name}`}
                  aria-label={`Add ${getLocationTypeLabel(childType).toLowerCase()} in ${node.name}`}
                  className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#8a8f96] opacity-70 transition hover:border-[#087f8c] hover:bg-[#087f8c] hover:text-white group-hover:opacity-100"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              ) : (
                <span className="h-6 w-6 shrink-0" />
              )}
            </div>

            {addParentId === node.id && onCreateLocation ? (
              <div
                className="py-1 pr-1.5"
                style={{ paddingLeft: 6 + indent + 18 }}
              >
                <AddStorageLocationForm
                  inline
                  parent={node}
                  submitting={creatingLocation}
                  onCancel={onCancelAdd}
                  onCreate={onCreateLocation}
                  onCreated={() => onCreated(node.id)}
                />
              </div>
            ) : null}

            {hasChildren && !isCollapsed ? (
              <StorageTree
                nodes={children}
                depth={depth + 1}
                collapsedIds={collapsedIds}
                addParentId={addParentId}
                creatingLocation={creatingLocation}
                selectedLocationId={selectedLocationId}
                selectionEnabled={selectionEnabled}
                onSelectLocation={onSelectLocation}
                onToggleCollapse={onToggleCollapse}
                onRequestAdd={onRequestAdd}
                onCancelAdd={onCancelAdd}
                onCreated={onCreated}
                onCreateLocation={onCreateLocation}
              />
            ) : null}
          </div>
        );
      })}
  </div>
);
